import React, { PureComponent, ReactNode } from "react";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import { KMainFunctions } from "../../../shared/utilty/main-functions";
import { CommonProps } from "../common/common-props";

// Pre-computed class strings to avoid template literal allocation on every render
const TABPANE_ACTIVE = "rc-tabs-tabpane rc-tabs-tabpane-active";
const TABPANE_INACTIVE = "rc-tabs-tabpane rc-tabs-tabpane-inactive";

declare let window: any;
interface TabItemProps {
  children?: ReactNode;
  style?: any;
  onActive?: () => void;
  forceRender?: boolean;
  icon?: string;
  iconColor?: string;
  // Internal props set by parent Tab
  _active?: boolean;
  _tabKey?: string;
  _parentId?: string;
}

interface TabItemState {}

class TabItem extends PureComponent<TabItemProps & CommonProps, TabItemState> {
  private containerRef = React.createRef<HTMLDivElement>();

  // Once rendered, keep children mounted to avoid expensive remount on tab switch.
  // Visibility is controlled via CSS (rc-tabs-tabpane-inactive { display: none }).
  private hasRendered = false;

  // Cached ARIA id strings — only recomputed when _tabKey or _parentId changes
  private cachedPaneId = "";
  private cachedTabId = "";
  private lastTabKey: string | undefined;
  private lastParentId: string | undefined;

  constructor(props: TabItemProps) {
    super(props);
    this.state = {};
  }

  componentDidMount(): void {
    this.setAlignments();
  }

  componentDidUpdate(
    prevProps: Readonly<TabItemProps & CommonProps>,
    prevState: Readonly<TabItemState>,
    snapshot?: any
  ): void {
    const prevStyle = prevProps.style;
    const currStyle = this.props.style;

    const alignmentChanged =
      prevStyle?.alignItems !== currStyle?.alignItems ||
      prevStyle?.justifyContent !== currStyle?.justifyContent ||
      prevStyle?.textAlign !== currStyle?.textAlign;

    if (alignmentChanged) {
      this.setAlignments();
    }
  }

  setAlignments = () => {
    const element = this.containerRef.current;
    if (!element) return;
    KMainFunctions.handleAlignments(element, this.props.style);
  };

  render(): ReactNode {
    const { _active, _tabKey, _parentId, forceRender, children } = this.props;
    const isActive = _active === true;
    const shouldRender = isActive || forceRender || window.kuika?.isDesignTime;

    // Once children are mounted, keep them alive to avoid costly remount on tab switch.
    if (shouldRender) this.hasRendered = true;

    // Cache ARIA id strings
    if (_tabKey !== this.lastTabKey || _parentId !== this.lastParentId) {
      this.cachedPaneId = _parentId ? `tabpane-${_tabKey}-${_parentId}` : `tabpane-${_tabKey}`;
      this.cachedTabId = _parentId ? `tab-${_tabKey}-${_parentId}` : `tab-${_tabKey}`;
      this.lastTabKey = _tabKey;
      this.lastParentId = _parentId;
    }

    return (
      <div
        role="tabpanel"
        className={isActive ? TABPANE_ACTIVE : TABPANE_INACTIVE}
        style={this.props.style}
        aria-hidden={!isActive}
        aria-labelledby={this.cachedTabId}
        id={this.cachedPaneId}
        tabIndex={isActive ? 0 : -1}
      >
        {this.hasRendered && <div ref={this.containerRef}>{children}</div>}
      </div>
    );
  }
}

const tabItem = withCommonEvents(TabItem);
export { tabItem as TabItem };
